import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Building2,
  Camera,
  Check,
  CircleUserRound,
  Headphones,
  ImagePlus,
  Eye,
  EyeOff,
  Search,
  Sparkles,
  UserRound,
} from "lucide-react";
import avatarAndre from "./assets/avatar-andre.png";
import avatarGarland from "./assets/avatar-garland.png";
import avatarMaya from "./assets/avatar-maya.png";
import avatarPriya from "./assets/avatar-priya.png";
import {
  billCollectorNotices,
  businessSectors,
  employeeBands,
  mindsetAccountExperienceAdapter,
  type AccountType,
  type BusinessProfileDraft,
  type DisplayNamePreference,
  type OnboardingSubmissionResult,
  type PersonalProfileDraft,
  type VibeTrackDraft,
} from "./accountExperienceAdapter";
import {
  MINDSET_BUSINESS_IDENTITY_PRICE,
  MINDSET_BUSINESS_WORKSPACE_PRICE,
} from "./businessPricing";
import { IntegrationApplications } from "./IntegrationApplications";
import "./accountExperience.css";
import "./accessRedesign.css";
import { consumeAuthReturn, rememberAuthReturn } from "./authReturn";
import { EmailAccessGate } from "./EmailCodeAccess";

type AccessMode = "signin" | "get-started";

const avatarPresets = [
  { id: "aura", label: "Aura", src: avatarMaya },
  { id: "nova", label: "Nova", src: avatarAndre },
  { id: "pulse", label: "Pulse", src: avatarPriya },
  { id: "orbit", label: "Orbit", src: avatarGarland },
] as const;

const personalSteps = ["Identity", "Profile", "Preferences", "Review"] as const;
const businessSteps = [
  "Owner identity",
  "Owner profile",
  "Preferences",
  "Company",
  "Review",
] as const;

const emptyPersonalProfile: PersonalProfileDraft = {
  legalFirstName: "",
  legalLastName: "",
  city: "",
  state: "",
  postalCode: "",
  handle: "",
  displayNamePreference: "FULL_NAME",
  avatarKind: "PRESET",
  avatarPreset: "aura",
  avatarUrl: "",
  highlight: "",
  bio: "",
  socials: {
    instagram: "",
    tiktok: "",
    snapchat: "",
    facebook: "",
    linkedin: "",
  },
  employer: "",
  employerVisibility: "PRIVATE",
  billCollectorNotice: "REMOVE_FROM_CALLING_SYSTEM",
  noSoliciting: false,
  vibeTrack: {
    provider: "",
    trackId: "",
    title: "",
    artist: "",
    artworkUrl: "",
    previewUrl: "",
  },
};

const emptyBusinessProfile: BusinessProfileDraft = {
  owner: emptyPersonalProfile,
  companyName: "",
  sectorCode: "",
  industry: "",
  employeeBand: "",
  expectedUsers: "",
  currentSystem: "",
  primaryUseCase: "",
  planInterest: "LITE",
};

export function AccountAccessPage({ header }: { header: ReactNode }) {
  useEffect(() => { rememberAuthReturn(); }, []);
  const [mode, setMode] = useState<AccessMode>(accessModeFromLocation);
  const [accountType, setAccountType] = useState<AccountType | null>(null);

  const changeMode = (nextMode: AccessMode) => {
    setMode(nextMode);
    if (nextMode === "signin") {
      setAccountType(null);
    }
  };

  return (
    <div className={`site-shell account-access-shell access-redesign ${mode === "signin" ? "access-redesign-signin" : "access-redesign-signup"}`}>
      {header}
      <main className="account-access-page" aria-label={mode === "signin" ? "Sign in" : "Create account"}>
        {mode === "signin" ? (
          <SignInPanel />
        ) : accountType === null ? (
          <AccountTypeChooser onChoose={setAccountType} />
        ) : accountType === "PERSONAL" ? (
          <PersonalSignupWizard onBack={() => setAccountType(null)} />
        ) : (
          <BusinessSignupWizard onBack={() => setAccountType(null)} />
        )}
        <p className="access-mode-link">
          {mode === "signin" ? "New to Mindset?" : "Already have an account?"}{" "}
          <button type="button" onClick={() => changeMode(mode === "signin" ? "get-started" : "signin")}>
            {mode === "signin" ? "Create account" : "Sign in"}
          </button>
        </p>
      </main>
    </div>
  );
}

export function VerifiedAccessCallbackPage({ header }: { header: ReactNode }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    mindsetAccountExperienceAdapter
      .completeVerifiedAccess(window.location.search)
      .then(({ mode }) => {
        if (!active) return;
        window.location.replace(mode === "register" ? "/access?mode=get-started" : consumeAuthReturn());
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : "Verified access could not be completed.");
        }
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="site-shell account-access-shell access-redesign">
      {header}
      <main className="account-access-page">
        <section className="access-provider-layout" aria-live="polite">
          <div className="access-provider-main">
            <div className="access-provider-icon" aria-hidden="true"><BadgeCheck size={28} /></div>
            <p className="account-kicker">Verified identity access</p>
            <h1>{error ? "Sign-in needs another try" : "Completing secure sign-in"}</h1>
            <p>{error ?? "Mindset is confirming your verified identity and opening your account."}</p>
            {error ? <a className="account-primary-button" href="/access?mode=signin">Return to sign in</a> : null}
          </div>
        </section>
      </main>
    </div>
  );
}

function SignInPanel() {
  return (
    <section className="access-provider-layout" aria-labelledby="signin-heading">
      <div className="access-provider-main">
        <p className="account-kicker">Mindset</p>
        <h1 id="signin-heading">Sign in</h1>
        <p>Welcome back. Pick up where you left off.</p>
        <FirebaseAccessForm mode="signin" />
      </div>
    </section>
  );
}

function AccountTypeChooser({ onChoose }: { onChoose: (type: AccountType) => void }) {
  return (
    <section className="signup-workspace" aria-labelledby="signup-account-type">
      <div className="signup-workspace-heading">
        <div>
          <p className="account-kicker">Mindset</p>
          <h1 id="signup-account-type">Create account</h1>
          <p>How will you use Mindset?</p>
        </div>
      </div>

      <div className="account-type-grid">
        <button type="button" onClick={() => onChoose("PERSONAL")}>
          <span className="account-type-icon" aria-hidden="true">
            <UserRound size={28} />
          </span>
          <strong>Personal</strong>
          <span>Create your handle, profile, calling preferences, and public identity.</span>
          <em>
            Continue as Personal
            <ArrowRight aria-hidden="true" size={17} />
          </em>
        </button>
        <button type="button" onClick={() => onChoose("BUSINESS")}>
          <span className="account-type-icon" aria-hidden="true">
            <Building2 size={28} />
          </span>
          <strong>Business</strong>
          <span>Set up a company workspace, team size, use case, and starting plan.</span>
          <em>
            Continue as Business
            <ArrowRight aria-hidden="true" size={17} />
          </em>
        </button>
      </div>

      <div className="account-pricing-note" aria-label="Mindset account pricing">
        <span>Individuals are free.</span>
        <span>Business @ identities are $12/year each.</span>
        <span>New company subscriptions are $36/year flat. Existing subscriptions are unchanged.</span>
      </div>

      <DraftStorageNotice />
    </section>
  );
}

function PersonalSignupWizard({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState<PersonalProfileDraft>(() => {
    const draft = mindsetAccountExperienceAdapter.loadOnboardingDraft("PERSONAL");
    return draft?.accountType === "PERSONAL" ? draft.profile : emptyPersonalProfile;
  });
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [submission, setSubmission] = useState<OnboardingSubmissionResult | null>(null);
  const [vibeQuery, setVibeQuery] = useState("");
  const vibeSearch = useVibeTrackSearch(vibeQuery);

  const updateProfile = <Key extends keyof PersonalProfileDraft>(
    key: Key,
    value: PersonalProfileDraft[Key],
  ) => {
    setProfile((current) => ({ ...current, [key]: value }));
  };

  const goNext = () => {
    const validationError = validatePersonalStep(step, profile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setStep((current) => Math.min(current + 1, personalSteps.length - 1));
  };

  const saveDraft = async () => {
    setSaveState("saving");
    setError("");
    try {
      const result = await mindsetAccountExperienceAdapter.submitOnboarding({
        accountType: "PERSONAL",
        profile,
      });
      setSubmission(result);
      setSaveState("saved");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Mindset could not save this profile.");
      setSaveState("idle");
    }
  };

  if (saveState === "saved" && submission) {
    return (
      <SignupComplete
        accountLabel="Personal profile"
        submission={submission}
        onAuthenticated={async () => {
          setSubmission(await mindsetAccountExperienceAdapter.submitOnboarding({
            accountType: "PERSONAL",
            profile,
          }));
        }}
      />
    );
  }

  return (
    <section className="signup-workspace" aria-labelledby="personal-signup-title">
      <WizardHeader
        title="Build your personal profile"
        eyebrow="Personal account"
        step={step}
        steps={personalSteps}
        onBack={onBack}
      />

      <form
        className="signup-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (step === personalSteps.length - 1) {
            void saveDraft();
          } else {
            goNext();
          }
        }}
      >
        {step === 0 ? (
          <PersonalIdentityStep profile={profile} updateProfile={updateProfile} />
        ) : null}
        {step === 1 ? (
          <PersonalProfileStep profile={profile} updateProfile={updateProfile} />
        ) : null}
        {step === 2 ? (
          <PersonalPreferencesStep
            profile={profile}
            updateProfile={updateProfile}
            vibeQuery={vibeQuery}
            setVibeQuery={setVibeQuery}
            visibleTracks={vibeSearch.tracks}
            vibeSearchStatus={vibeSearch.status}
          />
        ) : null}
        {step === 3 ? <PersonalReview profile={profile} /> : null}

        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <WizardActions
          canGoBack={step > 0}
          nextLabel={step === personalSteps.length - 1 ? "Complete profile" : "Continue"}
          saving={saveState === "saving"}
          onBack={() => setStep((current) => Math.max(0, current - 1))}
        />
      </form>
    </section>
  );
}

function PersonalIdentityStep({
  profile,
  updateProfile,
}: {
  profile: PersonalProfileDraft;
  updateProfile: <Key extends keyof PersonalProfileDraft>(
    key: Key,
    value: PersonalProfileDraft[Key],
  ) => void;
}) {
  return (
    <fieldset className="form-section">
      <legend>Legal identity and home location</legend>
      <p className="form-section-copy">
        Legal names support verified account ownership. Your city, state, and postal
        code are private by default.
      </p>
      <div className="form-grid form-grid--two">
        <TextField
          label="Legal first name"
          name="legalFirstName"
          value={profile.legalFirstName}
          onChange={(value) => updateProfile("legalFirstName", value)}
          autoComplete="given-name"
          required
        />
        <TextField
          label="Legal last name"
          name="legalLastName"
          value={profile.legalLastName}
          onChange={(value) => updateProfile("legalLastName", value)}
          autoComplete="family-name"
          required
        />
      </div>
      <div className="form-grid form-grid--location">
        <TextField
          label="City"
          name="city"
          value={profile.city}
          onChange={(value) => updateProfile("city", value)}
          autoComplete="address-level2"
          required
        />
        <TextField
          label="State"
          name="state"
          value={profile.state}
          onChange={(value) => updateProfile("state", value)}
          autoComplete="address-level1"
          required
        />
        <TextField
          label="Postal code"
          name="postalCode"
          value={profile.postalCode}
          onChange={(value) => updateProfile("postalCode", value)}
          autoComplete="postal-code"
          required
        />
      </div>
    </fieldset>
  );
}

function PersonalProfileStep({
  profile,
  updateProfile,
}: {
  profile: PersonalProfileDraft;
  updateProfile: <Key extends keyof PersonalProfileDraft>(
    key: Key,
    value: PersonalProfileDraft[Key],
  ) => void;
}) {
  const [avatarUploadState, setAvatarUploadState] = useState<
    "idle" | "uploading" | "ready" | "error"
  >("idle");
  const [avatarUploadMessage, setAvatarUploadMessage] = useState("");

  const uploadAvatar = async (file: File | undefined) => {
    if (!file) return;
    setAvatarUploadState("uploading");
    setAvatarUploadMessage("Uploading photo...");
    try {
      const result = await mindsetAccountExperienceAdapter.uploadProfileAvatar(file);
      updateProfile("avatarKind", "UPLOAD");
      updateProfile("avatarPreset", "");
      updateProfile("avatarUrl", result.avatarUrl);
      setAvatarUploadState("ready");
      setAvatarUploadMessage("Profile photo ready");
    } catch (uploadError) {
      setAvatarUploadState("error");
      setAvatarUploadMessage(
        uploadError instanceof Error ? uploadError.message : "Unable to upload that photo.",
      );
    }
  };

  return (
    <div className="form-stack">
      <fieldset className="form-section">
        <legend>Your public identity</legend>
        <div className="form-grid form-grid--two">
          <label className="form-field">
            <span>Handle</span>
            <div className="handle-field">
              <span aria-hidden="true">@</span>
              <input
                aria-label="Handle"
                autoCapitalize="none"
                autoComplete="username"
                name="handle"
                required
                value={profile.handle.replace(/^@/u, "")}
                onChange={(event) =>
                  updateProfile(
                    "handle",
                    event.target.value.toLowerCase().replace(/[^a-z0-9._]/gu, ""),
                  )
                }
              />
            </div>
            <small>Letters, numbers, periods, and underscores.</small>
          </label>
          <label className="form-field">
            <span>Profile highlight</span>
            <input
              aria-label="Profile highlight"
              maxLength={40}
              name="highlight"
              placeholder="A short line that feels like you"
              required
              value={profile.highlight}
              onChange={(event) => updateProfile("highlight", event.target.value)}
            />
            <small>{profile.highlight.length}/40 characters</small>
          </label>
        </div>

        <fieldset className="choice-fieldset">
          <legend>What should people see?</legend>
          <div className="choice-row" role="radiogroup" aria-label="Display name">
            {(
              [
                ["FULL_NAME", "Full name"],
                ["FIRST_NAME", "First name"],
                ["HANDLE_ONLY", "Handle only"],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="choice-option">
                <input
                  type="radio"
                  name="displayNamePreference"
                  value={value}
                  checked={profile.displayNamePreference === value}
                  onChange={() => updateProfile("displayNamePreference", value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </fieldset>

      <fieldset className="form-section">
        <legend>Profile picture</legend>
        <p className="form-section-copy">Choose a Mindset avatar or upload your own image.</p>
        <div className="avatar-chooser" role="radiogroup" aria-label="Profile avatar">
          {avatarPresets.map((avatar) => (
            <button
              key={avatar.id}
              type="button"
              role="radio"
              aria-checked={profile.avatarKind === "PRESET" && profile.avatarPreset === avatar.id}
              aria-label={`Use ${avatar.label} avatar`}
              className={profile.avatarKind === "PRESET" && profile.avatarPreset === avatar.id ? "is-selected" : ""}
              onClick={() => {
                updateProfile("avatarKind", "PRESET");
                updateProfile("avatarPreset", avatar.id);
                updateProfile("avatarUrl", "");
              }}
            >
              <img src={avatar.src} alt="" />
              <span>{avatar.label}</span>
              {profile.avatarKind === "PRESET" && profile.avatarPreset === avatar.id ? (
                <Check aria-hidden="true" size={16} />
              ) : null}
            </button>
          ))}
          <label
            className={`avatar-upload ${profile.avatarKind === "UPLOAD" ? "is-selected" : ""}`}
            role="radio"
            aria-checked={profile.avatarKind === "UPLOAD"}
            aria-label="Use uploaded profile picture"
          >
            <input
              accept="image/jpeg,image/png,image/webp"
              aria-label="Upload profile picture"
              disabled={avatarUploadState === "uploading"}
              type="file"
              onChange={(event) => {
                void uploadAvatar(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
            <ImagePlus aria-hidden="true" size={24} />
            <span>{avatarUploadState === "uploading" ? "Uploading..." : "Upload photo"}</span>
            {profile.avatarKind === "UPLOAD" ? <Check aria-hidden="true" size={16} /> : null}
          </label>
        </div>
        {avatarUploadMessage ? (
          <p
            className={`avatar-upload-status avatar-upload-status--${avatarUploadState}`}
            role={avatarUploadState === "error" ? "alert" : "status"}
          >
            {avatarUploadMessage}
          </p>
        ) : null}
      </fieldset>

      <fieldset className="form-section">
        <legend>About you</legend>
        <label className="form-field">
          <span>Bio <em>Optional</em></span>
          <textarea
            aria-label="Bio"
            name="bio"
            maxLength={280}
            rows={4}
            placeholder="Share more about yourself."
            value={profile.bio}
            onChange={(event) => updateProfile("bio", event.target.value)}
          />
          <small>{profile.bio.length}/280 characters</small>
        </label>
      </fieldset>
    </div>
  );
}

function PersonalPreferencesStep({
  profile,
  updateProfile,
  vibeQuery,
  setVibeQuery,
  visibleTracks,
  vibeSearchStatus,
}: {
  profile: PersonalProfileDraft;
  updateProfile: <Key extends keyof PersonalProfileDraft>(
    key: Key,
    value: PersonalProfileDraft[Key],
  ) => void;
  vibeQuery: string;
  setVibeQuery: (value: string) => void;
  visibleTracks: readonly {
    provider: string;
    trackId: string;
    title: string;
    artist: string;
    artworkUrl: string;
    previewUrl: string;
  }[];
  vibeSearchStatus: "idle" | "searching" | "ready" | "error";
}) {
  const updateSocial = (key: keyof PersonalProfileDraft["socials"], value: string) => {
    updateProfile("socials", { ...profile.socials, [key]: value });
  };

  return (
    <div className="form-stack">
      <fieldset className="form-section">
        <legend>Social profiles <span>Optional</span></legend>
        <div className="form-grid form-grid--social">
          <SocialField label="Instagram" value={profile.socials.instagram} onChange={(value) => updateSocial("instagram", value)} />
          <SocialField label="TikTok" value={profile.socials.tiktok} onChange={(value) => updateSocial("tiktok", value)} />
          <SocialField label="Snapchat" value={profile.socials.snapchat} onChange={(value) => updateSocial("snapchat", value)} />
          <SocialField label="Facebook" value={profile.socials.facebook} onChange={(value) => updateSocial("facebook", value)} />
          <SocialField label="LinkedIn" value={profile.socials.linkedin} onChange={(value) => updateSocial("linkedin", value)} />
        </div>
      </fieldset>

      <fieldset className="form-section">
        <legend>Work <span>Optional</span></legend>
        <div className="form-grid form-grid--two">
          <TextField
            label="Employer"
            name="employer"
            value={profile.employer}
            onChange={(value) => updateProfile("employer", value)}
            placeholder="Company or organization"
          />
          <label className="form-field">
            <span>Employer visibility</span>
            <select
              aria-label="Employer visibility"
              value={profile.employerVisibility}
              onChange={(event) =>
                updateProfile("employerVisibility", event.target.value as "PUBLIC" | "PRIVATE")
              }
            >
              <option value="PRIVATE">Private</option>
              <option value="PUBLIC">Public</option>
            </select>
          </label>
        </div>
      </fieldset>

      <fieldset className="form-section">
        <legend>Communication boundaries</legend>
        <label className="form-field">
          <span>Bill collector communication notice</span>
          <select
            aria-label="Bill collector communication notice"
            value={profile.billCollectorNotice}
            onChange={(event) =>
              updateProfile(
                "billCollectorNotice",
                event.target.value as PersonalProfileDraft["billCollectorNotice"],
              )
            }
          >
            {billCollectorNotices.map((notice) => (
              <option key={notice.value} value={notice.value}>{notice.label}</option>
            ))}
          </select>
          <small>This preference is shown as a communication request, not legal advice.</small>
        </label>
        <label className="settings-toggle-row">
          <span>
            <strong>No soliciting</strong>
            <small>Block business calls that declare a solicitation purpose.</small>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-label="No soliciting"
            checked={profile.noSoliciting}
            onChange={(event) => updateProfile("noSoliciting", event.target.checked)}
          />
        </label>
      </fieldset>

      <fieldset className="form-section">
        <legend>Vibe track <span>Optional</span></legend>
        <p className="form-section-copy">Choose the song visitors can play from your profile.</p>
        <label className="vibe-search-field">
          <Search aria-hidden="true" size={19} />
          <input
            aria-label="Search vibe tracks"
            value={vibeQuery}
            placeholder="Search a track or artist"
            onChange={(event) => setVibeQuery(event.target.value)}
          />
        </label>
        <div className="vibe-results" aria-label="Vibe track results">
          {visibleTracks.map((track) => {
            const selected =
              profile.vibeTrack.title === track.title && profile.vibeTrack.artist === track.artist;
            return (
              <button
                key={`${track.provider}:${track.trackId}`}
                type="button"
                className={selected ? "is-selected" : ""}
                onClick={() => updateProfile("vibeTrack", {
                  provider: track.provider,
                  trackId: track.trackId,
                  title: track.title,
                  artist: track.artist,
                  artworkUrl: track.artworkUrl,
                  previewUrl: track.previewUrl,
                })}
              >
                <span aria-hidden="true"><Headphones size={18} /></span>
                <strong>{track.title}</strong>
                <em>{track.artist}</em>
                {selected ? <Check aria-hidden="true" size={17} /> : null}
              </button>
            );
          })}
        </div>
        <p className="vibe-search-status" role="status">
          {vibeSearchStatus === "searching"
            ? "Searching music..."
            : vibeSearchStatus === "error"
              ? "Music search is temporarily unavailable."
              : vibeQuery.trim().length < 2
                ? "Enter at least two characters to search."
                : visibleTracks.length === 0
                  ? "No tracks found."
                  : `${visibleTracks.length} track${visibleTracks.length === 1 ? "" : "s"} found`}
        </p>
        {profile.vibeTrack.title ? (
          <div className="selected-vibe-fields" aria-label="Selected vibe track">
            <Headphones aria-hidden="true" size={18} />
            <span><strong>{profile.vibeTrack.title}</strong><small>{profile.vibeTrack.artist}</small></span>
            <button type="button" onClick={() => updateProfile("vibeTrack", emptyPersonalProfile.vibeTrack)}>
              Remove
            </button>
          </div>
        ) : null}
      </fieldset>
    </div>
  );
}

function PersonalReview({ profile }: { profile: PersonalProfileDraft }) {
  const displayLabels: Record<DisplayNamePreference, string> = {
    FULL_NAME: "Full name",
    FIRST_NAME: "First name",
    HANDLE_ONLY: "Handle only",
  };

  return (
    <div className="review-layout">
      <section className="profile-review-preview" aria-label="Profile preview">
        <div className="profile-review-avatar">
          {profile.avatarKind === "UPLOAD" ? <Camera aria-hidden="true" size={28} /> : <CircleUserRound aria-hidden="true" size={32} />}
        </div>
        <div>
          <p className="account-kicker">Profile preview</p>
          <h3>{profile.legalFirstName} {profile.legalLastName}</h3>
          <strong>@{profile.handle.replace(/^@/u, "")}</strong>
          <p>{profile.highlight}</p>
        </div>
      </section>
      <dl className="review-details">
        <div><dt>Display</dt><dd>{displayLabels[profile.displayNamePreference]}</dd></div>
        <div><dt>Location</dt><dd>{profile.city}, {profile.state} {profile.postalCode}</dd></div>
        <div><dt>Employer</dt><dd>{profile.employer || "Not added"} ({profile.employerVisibility.toLowerCase()})</dd></div>
        <div><dt>No soliciting</dt><dd>{profile.noSoliciting ? "On" : "Off"}</dd></div>
        <div><dt>Vibe track</dt><dd>{profile.vibeTrack.title ? `${profile.vibeTrack.title} - ${profile.vibeTrack.artist}` : "Not selected"}</dd></div>
      </dl>
      <DraftStorageNotice />
    </div>
  );
}

function BusinessSignupWizard({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState<BusinessProfileDraft>(() => {
    const draft = mindsetAccountExperienceAdapter.loadOnboardingDraft("BUSINESS");
    return draft?.accountType === "BUSINESS" ? draft.profile : emptyBusinessProfile;
  });
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [submission, setSubmission] = useState<OnboardingSubmissionResult | null>(null);
  const [vibeQuery, setVibeQuery] = useState("");
  const vibeSearch = useVibeTrackSearch(vibeQuery);

  const updateProfile = <Key extends keyof BusinessProfileDraft>(
    key: Key,
    value: BusinessProfileDraft[Key],
  ) => setProfile((current) => ({ ...current, [key]: value }));

  const updateOwner = <Key extends keyof PersonalProfileDraft>(
    key: Key,
    value: PersonalProfileDraft[Key],
  ) => setProfile((current) => ({
    ...current,
    owner: { ...current.owner, [key]: value },
  }));

  const goNext = () => {
    const validationError = step <= 2
      ? validatePersonalStep(step, profile.owner)
      : step === 3
        ? validateBusinessCompany(profile)
        : "";
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setStep((current) => Math.min(current + 1, businessSteps.length - 1));
  };

  const saveDraft = async () => {
    setSaveState("saving");
    setError("");
    try {
      const result = await mindsetAccountExperienceAdapter.submitOnboarding({
        accountType: "BUSINESS",
        profile,
      });
      setSubmission(result);
      setSaveState("saved");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Mindset could not save this company account.");
      setSaveState("idle");
    }
  };

  if (saveState === "saved" && submission) {
    return (
      <SignupComplete
        accountLabel="Business workspace"
        submission={submission}
        integrationHandoff={submission.status === "SUBMITTED" && submission.company
          ? { companyId: submission.company.id, userId: submission.profile.userId }
          : undefined}
        onAuthenticated={async () => {
          setSubmission(await mindsetAccountExperienceAdapter.submitOnboarding({
            accountType: "BUSINESS",
            profile,
          }));
        }}
      />
    );
  }

  return (
    <section className="signup-workspace" aria-labelledby="business-signup-title">
      <WizardHeader
        title="Set up your company and owner profile"
        eyebrow="Business account"
        step={step}
        steps={businessSteps}
        onBack={onBack}
      />
      <form
        className="signup-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (step === businessSteps.length - 1) {
            void saveDraft();
          } else {
            goNext();
          }
        }}
      >
        {step === 0 ? (
          <PersonalIdentityStep profile={profile.owner} updateProfile={updateOwner} />
        ) : null}
        {step === 1 ? (
          <PersonalProfileStep profile={profile.owner} updateProfile={updateOwner} />
        ) : null}
        {step === 2 ? (
          <PersonalPreferencesStep
            profile={profile.owner}
            updateProfile={updateOwner}
            vibeQuery={vibeQuery}
            setVibeQuery={setVibeQuery}
            visibleTracks={vibeSearch.tracks}
            vibeSearchStatus={vibeSearch.status}
          />
        ) : null}
        {step === 3 ? <BusinessCompanyStep profile={profile} updateProfile={updateProfile} /> : null}
        {step === 4 ? <BusinessReview profile={profile} updateProfile={updateProfile} /> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <WizardActions
          canGoBack={step > 0}
          nextLabel={step === businessSteps.length - 1 ? "Complete business account" : "Continue"}
          saving={saveState === "saving"}
          onBack={() => setStep((current) => Math.max(0, current - 1))}
        />
      </form>
    </section>
  );
}

function BusinessCompanyStep({
  profile,
  updateProfile,
}: {
  profile: BusinessProfileDraft;
  updateProfile: <Key extends keyof BusinessProfileDraft>(
    key: Key,
    value: BusinessProfileDraft[Key],
  ) => void;
}) {
  return (
    <fieldset className="form-section">
      <legend>Company details</legend>
      <p className="form-section-copy">
        These fields establish the first organization. Owners can add more companies and members later.
      </p>
      <div className="form-grid form-grid--two">
        <TextField label="Company name" name="companyName" value={profile.companyName} onChange={(value) => updateProfile("companyName", value)} required />
        <label className="form-field">
          <span>Sector</span>
          <select aria-label="Sector" value={profile.sectorCode} required onChange={(event) => updateProfile("sectorCode", event.target.value)}>
            <option value="">Select a sector</option>
            {businessSectors.map((sector) => <option key={sector.value} value={sector.value}>{sector.label}</option>)}
          </select>
        </label>
        <TextField label="Industry" name="industry" placeholder="Your specific industry" value={profile.industry} onChange={(value) => updateProfile("industry", value)} required />
        <label className="form-field">
          <span>Employee band</span>
          <select aria-label="Employee band" value={profile.employeeBand} required onChange={(event) => updateProfile("employeeBand", event.target.value)}>
            <option value="">Select company size</option>
            {employeeBands.map((band) => <option key={band} value={band}>{band}</option>)}
          </select>
        </label>
        <TextField label="Expected users" name="expectedUsers" inputMode="numeric" placeholder="25" value={profile.expectedUsers} onChange={(value) => updateProfile("expectedUsers", value.replace(/[^0-9]/gu, ""))} required />
        <TextField label="Current communication system" name="currentSystem" placeholder="Teams, RingCentral, none..." value={profile.currentSystem} onChange={(value) => updateProfile("currentSystem", value)} required />
      </div>
      <label className="form-field">
        <span>Primary use case</span>
        <textarea aria-label="Primary use case" rows={4} required placeholder="Tell us how your teams or customers will use Mindset." value={profile.primaryUseCase} onChange={(event) => updateProfile("primaryUseCase", event.target.value)} />
      </label>
    </fieldset>
  );
}

function BusinessReview({
  profile,
  updateProfile,
}: {
  profile: BusinessProfileDraft;
  updateProfile: <Key extends keyof BusinessProfileDraft>(
    key: Key,
    value: BusinessProfileDraft[Key],
  ) => void;
}) {
  const sector = businessSectors.find((item) => item.value === profile.sectorCode)?.label ?? profile.sectorCode;
  return (
    <div className="business-review-layout">
      <fieldset className="form-section plan-choice-section">
        <legend>Choose a starting plan</legend>
        <p className="form-section-copy">New business subscriptions are $36/year per company plus $12/year per business-owned @handle. Individuals are free. Existing subscriptions are unchanged. Payment and ownership verification follow account creation.</p>
        <div className="plan-choice-grid" role="radiogroup" aria-label="Starting plan">
          <label className={profile.planInterest === "LITE" ? "is-selected" : ""}>
            <input aria-label="Workspace review" type="radio" name="planInterest" value="LITE" checked={profile.planInterest === "LITE"} onChange={() => updateProfile("planInterest", "LITE")} />
            <span>Workspace review</span>
            <strong>Set up before subscribing</strong>
            <p>Save your company and application details before choosing a subscription.</p>
            <small>No payment or activation yet</small>
          </label>
          <label className={profile.planInterest === "PAID" ? "is-selected" : ""}>
            <input aria-label="Business Identity" type="radio" name="planInterest" value="PAID" checked={profile.planInterest === "PAID"} onChange={() => updateProfile("planInterest", "PAID")} />
            <span>Business Identity</span>
            <strong>{MINDSET_BUSINESS_IDENTITY_PRICE.displayPrice}</strong>
            <p>Annual company subscription with business-owned @ handles. Activation requires verified payment and application ownership.</p>
            <small>Team communications: {MINDSET_BUSINESS_WORKSPACE_PRICE.displayPrice}</small>
          </label>
        </div>
      </fieldset>
      <dl className="review-details">
        <div><dt>Owner</dt><dd>{profile.owner.legalFirstName} {profile.owner.legalLastName} ({`@${profile.owner.handle.replace(/^@/u, "")}`})</dd></div>
        <div><dt>Company</dt><dd>{profile.companyName}</dd></div>
        <div><dt>Sector / industry</dt><dd>{sector} / {profile.industry}</dd></div>
        <div><dt>Team</dt><dd>{profile.employeeBand} employees, {profile.expectedUsers} expected users</dd></div>
        <div><dt>Current system</dt><dd>{profile.currentSystem}</dd></div>
        <div><dt>Plan path</dt><dd>{profile.planInterest === "LITE" ? "Start in workspace review" : `${MINDSET_BUSINESS_IDENTITY_PRICE.displayPrice}; team workspace ${MINDSET_BUSINESS_WORKSPACE_PRICE.displayPrice}`}</dd></div>
      </dl>
      <DraftStorageNotice />
    </div>
  );
}

function WizardHeader({
  title,
  eyebrow,
  step,
  steps,
  onBack,
}: {
  title: string;
  eyebrow: string;
  step: number;
  steps: readonly string[];
  onBack: () => void;
}) {
  return (
    <div className="wizard-header">
      <button className="icon-text-button" type="button" onClick={onBack}>
        <ArrowLeft aria-hidden="true" size={17} />
        <span>Account type</span>
      </button>
      <div>
        <p className="account-kicker">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <ol className="wizard-progress" aria-label="Signup progress">
        {steps.map((label, index) => (
          <li key={label} className={index === step ? "is-current" : index < step ? "is-complete" : ""}>
            <span>{index < step ? <Check aria-hidden="true" size={14} /> : index + 1}</span>
            <em>{label}</em>
          </li>
        ))}
      </ol>
    </div>
  );
}

function WizardActions({
  canGoBack,
  nextLabel,
  saving,
  onBack,
}: {
  canGoBack: boolean;
  nextLabel: string;
  saving: boolean;
  onBack: () => void;
}) {
  return (
    <div className="wizard-actions">
      {canGoBack ? (
        <button className="account-secondary-button" type="button" onClick={onBack}>
          <ArrowLeft aria-hidden="true" size={17} />
          <span>Back</span>
        </button>
      ) : <span />}
      <button className="account-primary-button" type="submit" disabled={saving}>
        <span>{saving ? "Saving..." : nextLabel}</span>
        <ArrowRight aria-hidden="true" size={17} />
      </button>
    </div>
  );
}

function SignupComplete({
  accountLabel,
  submission,
  onAuthenticated,
  integrationHandoff,
}: {
  accountLabel: string;
  submission: OnboardingSubmissionResult;
  onAuthenticated: () => Promise<void>;
  integrationHandoff?: { companyId: string; userId: string };
}) {
  const submitted = submission.status === "SUBMITTED";
  return (
    <section className={`signup-complete${integrationHandoff ? " signup-complete--integration" : ""}`} aria-labelledby="signup-complete-title">
      <span aria-hidden="true"><Check size={30} /></span>
      <p className="account-kicker">{submitted ? "Mindset account ready" : "Draft preserved"}</p>
      <h2 id="signup-complete-title">
        {accountLabel} {submitted ? "created" : "draft saved"}
      </h2>
      <p>
        {integrationHandoff
          ? `Choose the business identity checkout, register your own application, and complete verification to receive tenant configuration and SDK delivery. Business @ identities are ${MINDSET_BUSINESS_IDENTITY_PRICE.displayPrice}; team communications stay ${MINDSET_BUSINESS_WORKSPACE_PRICE.displayPrice}.`
          : submitted
          ? "Your verified session submitted this account to Mindset. Open the dashboard to review the saved profile and company access."
          : "Your entries are saved in this browser tab. Create your secure Mindset sign-in to submit this profile without re-entering it."}
      </p>
      {integrationHandoff ? (
        <>
          <a className="account-primary-button" href="/tenant-admin?view=setup"><span>Open company dashboard</span><ArrowRight size={17} aria-hidden="true" /></a>
          <IntegrationApplications companyId={integrationHandoff.companyId} userId={integrationHandoff.userId} />
        </>
      ) : <div className="signup-complete-actions">
        {submitted ? (
          <a className="account-primary-button" href="/dashboard">
            <span>Open dashboard</span>
            <ArrowRight aria-hidden="true" size={17} />
          </a>
        ) : (
          <FirebaseAccessForm mode="register" onAuthenticated={onAuthenticated} />
        )}
      </div>}
    </section>
  );
}

function FirebaseAccessForm({
  mode,
  onAuthenticated,
}: {
  mode: "signin" | "register";
  onAuthenticated?: () => Promise<void>;
}) {
  return <EmailAccessGate onAuthenticated={onAuthenticated}><LegacyFirebaseAccessForm mode={mode} onAuthenticated={onAuthenticated} /></EmailAccessGate>;
}

function LegacyFirebaseAccessForm({ mode, onAuthenticated }: { mode: "signin" | "register"; onAuthenticated?: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  const passwordId = useId();
  const errorId = useId();
  const submitting = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = mode === "signin" ? "Sign in" : "Create secure sign-in";

  return (
    <form
      className="firebase-access-form"
      aria-busy={pending}
      onSubmit={(event) => {
        event.preventDefault();
        if (submitting.current) return;
        if (recovering && cooldown > 0) return;
        submitting.current = true;
        setPending(true);
        setError(null);
        if (recovering) {
          setCooldown(30);
          setResetRequested(false);
          void mindsetAccountExperienceAdapter.resetFirebasePassword(email)
            .then(() => setResetRequested(true))
            .catch(() => setError("Unable to request a reset right now. Wait a moment and try again."))
            .finally(() => { submitting.current = false; setPending(false); });
          return;
        }
        const authenticate = mode === "signin"
          ? mindsetAccountExperienceAdapter.signInWithFirebase(email, password)
          : mindsetAccountExperienceAdapter.registerWithFirebase(email, password);
        void authenticate
          .then(async () => {
            if (onAuthenticated) {
              await onAuthenticated();
              return;
            }
            window.location.assign(consumeAuthReturn());
          })
          .catch((reason: unknown) => {
            submitting.current = false;
            setPending(false);
            setError(reason instanceof Error ? reason.message : "Secure sign-in is unavailable.");
          });
      }}
    >
      {recovering ? <p className="access-recovery-intro">Enter your account email to request a password reset.</p> : null}
      <label className="form-field">
        <span>Email</span>
        <input
          autoComplete="email"
          inputMode="email"
          name="email"
          disabled={pending}
          aria-describedby={error ? errorId : undefined}
          required
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      {!recovering ? <div className="form-field">
        <label htmlFor={passwordId}>Password</label>
        <div className="access-password-field">
        <input
          id={passwordId}
          autoComplete={mode === "signin" ? "current-password" : "new-password"}
          minLength={6}
          name="password"
          required
          type={showPassword ? "text" : "password"}
          disabled={pending}
          aria-describedby={error ? errorId : undefined}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button type="button" className="access-password-toggle"
          aria-label={showPassword ? "Hide password" : "Show password"}
          title={showPassword ? "Hide password" : "Show password"}
          aria-controls={passwordId} aria-pressed={showPassword}
          onClick={() => setShowPassword((visible) => !visible)}>
          {showPassword ? <EyeOff size={19} aria-hidden="true" /> : <Eye size={19} aria-hidden="true" />}
        </button>
        </div>
      </div> : null}
      {resetRequested && recovering ? <p role="status" className="access-recovery-confirmation">If an account can receive a reset email, you will receive a link shortly. Check your inbox and spam folder.</p> : null}
      <button
        className="account-primary-button"
        type="submit"
        disabled={pending || (recovering && cooldown > 0)}
      >
        <BadgeCheck aria-hidden="true" size={18} />
        <span>{recovering ? pending ? "Requesting reset" : cooldown > 0 ? `Try again in ${cooldown}s` : "Send reset link" : pending ? "Securing your session" : label}</span>
        <ArrowRight aria-hidden="true" size={18} />
      </button>
      {mode === "signin" ? <button type="button" className="access-recovery-link" disabled={pending}
        onClick={() => { setRecovering((value) => !value); setError(null); }}>
        {recovering ? "Back to sign in" : "Forgot password?"}
      </button> : null}
      {pending ? <span className="visually-hidden" role="status">{recovering ? "Requesting reset" : "Securing your session"}</span> : null}
      {error ? <p id={errorId} className="field-error" role="alert">{error}</p> : null}
    </form>
  );
}

function DraftStorageNotice() {
  return (
    <div className="draft-storage-notice" role="note">
      <Sparkles aria-hidden="true" size={18} />
      <p><strong>Continue without losing your work:</strong> entries stay in this browser tab before sign-in and are submitted only with an authenticated Mindset session.</p>
    </div>
  );
}

function TextField({
  label,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  autoComplete,
  inputMode,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: "text" | "numeric";
}) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <input
        name={name}
        value={value}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SocialField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="form-field social-field">
      <span>{label}</span>
      <div><span aria-hidden="true">@</span><input aria-label={`${label} handle`} value={value.replace(/^@/u, "")} onChange={(event) => onChange(event.target.value.replace(/^@/u, ""))} /></div>
    </label>
  );
}

function useVibeTrackSearch(query: string): {
  tracks: VibeTrackDraft[];
  status: "idle" | "searching" | "ready" | "error";
} {
  const [tracks, setTracks] = useState<VibeTrackDraft[]>([]);
  const [status, setStatus] = useState<"idle" | "searching" | "ready" | "error">("idle");

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < 2) {
      setTracks([]);
      setStatus("idle");
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setStatus("searching");
      void mindsetAccountExperienceAdapter.searchVibeTracks(normalized)
        .then((results) => {
          if (!active) return;
          setTracks(results);
          setStatus("ready");
        })
        .catch(() => {
          if (!active) return;
          setTracks([]);
          setStatus("error");
        });
    }, 320);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  return { tracks, status };
}

function validatePersonalStep(step: number, profile: PersonalProfileDraft) {
  if (step === 0) {
    const missingIdentity = [profile.legalFirstName, profile.legalLastName, profile.city, profile.state, profile.postalCode]
      .some((value) => !value.trim());
    return missingIdentity ? "Complete every legal identity and location field." : "";
  }
  if (step === 1) {
    if (!profile.handle.trim()) return "Choose a Mindset handle.";
    if (!profile.highlight.trim()) return "Add a profile highlight.";
    if (profile.avatarKind === "UPLOAD" && !isHttpsUrl(profile.avatarUrl)) {
      return "Upload a profile picture before continuing.";
    }
  }
  if (step === 2 && profile.vibeTrack.title) {
    const requiredTrackFields = [
      profile.vibeTrack.provider,
      profile.vibeTrack.trackId,
      profile.vibeTrack.title,
      profile.vibeTrack.artist,
    ];
    if (requiredTrackFields.some((value) => !value.trim())) {
      return "Choose a complete vibe track.";
    }
  }
  return "";
}

function validateBusinessCompany(profile: BusinessProfileDraft) {
  const missing = [
    profile.companyName,
    profile.sectorCode,
    profile.industry,
    profile.employeeBand,
    profile.expectedUsers,
    profile.currentSystem,
    profile.primaryUseCase,
  ].some((value) => !value.trim());
  return missing ? "Complete every company field before continuing." : "";
}

function isHttpsUrl(value: string) {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function accessModeFromLocation(): AccessMode {
  if (typeof window === "undefined") return "signin";
  return new URLSearchParams(window.location.search).get("mode") === "get-started"
    ? "get-started"
    : "signin";
}
